import React, { useState, useMemo } from "react";
import {
  Stack,
  Typography,
  Divider,
  Box,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  alpha,
} from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import axios from "axios";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { updateDocumentHeader } from "../redux/actions/documentHeaderActions";
import { cleanFormData } from "../helpers/formHelpers";
import { documentHeaderActions } from "../redux/slices/documentHeader";

import { DocumentHeaderResponseDto } from "../shared/dtos/document/DocumentHeaderResponseDto";
import { getErrorMessage } from "../utils/appUtils";

interface UpdateDocumentHeaderFormProps {
  documentHeader: DocumentHeaderResponseDto;
  loading: boolean;
  error: string | null;
}

interface DocumentHeaderFormValues {
  companySnapshot: {
    companyName: string;
    address: string;
    phone: string;
    email: string;
    taxId: string;
    logoUrl?: string;
  };
  templateMetadata: {
    theme: {
      primaryColor: string;
      secondaryColor: string;
      showLogo: boolean;
    };
    labels: {
      documentTitle: string;
      taxName: string;
    };
    toggles: {
      format: string;
      showItemDescription: boolean;
      showTaxColumn: boolean;
      showBankDetails: boolean;
    };
    footer: {
      thankYouMessage: string;
      bankDetails: string;
      termsAndConditions: string;
    };
  };
}

const validationSchema: Yup.ObjectSchema<DocumentHeaderFormValues> =
  Yup.object().shape({
    companySnapshot: Yup.object().shape({
      companyName: Yup.string().required("Company name is required"),
      address: Yup.string().required("Address is required"),
      phone: Yup.string().required("Phone is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      taxId: Yup.string().required("Tax ID is required"),
      logoUrl: Yup.string().url("Invalid URL"),
    }),
    templateMetadata: Yup.object().shape({
      theme: Yup.object().shape({
        primaryColor: Yup.string().required(),
        secondaryColor: Yup.string().required(),
        showLogo: Yup.boolean().required(),
      }),
      labels: Yup.object().shape({
        documentTitle: Yup.string().required(),
        taxName: Yup.string().required(),
      }),
      toggles: Yup.object().shape({
        format: Yup.string().required(),
        showItemDescription: Yup.boolean().required(),
        showTaxColumn: Yup.boolean().required(),
        showBankDetails: Yup.boolean().required(),
      }),
      footer: Yup.object().shape({
        thankYouMessage: Yup.string().required(),
        bankDetails: Yup.string().defined(),
        termsAndConditions: Yup.string().defined(),
      }),
    }),
  });

const UpdateDocumentHeaderForm: React.FC<UpdateDocumentHeaderFormProps> = ({
  documentHeader,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>("");

  // Safely extract metadata with defaults
  const initialValues: DocumentHeaderFormValues = useMemo(() => {
    const meta = (documentHeader?.templateMetadata as any) || {};
    const theme = meta.theme || {};
    const labels = meta.labels || {};
    const toggles = meta.toggles || {};
    const footer = meta.footer || {};

    return {
      companySnapshot: {
        companyName: documentHeader?.companySnapshot?.companyName || "",
        address: documentHeader?.companySnapshot?.address || "",
        phone: documentHeader?.companySnapshot?.phone || "",
        email: documentHeader?.companySnapshot?.email || "",
        taxId: documentHeader?.companySnapshot?.taxId || "",
        logoUrl: documentHeader?.companySnapshot?.logoUrl || "",
      },
      templateMetadata: {
        theme: {
          primaryColor: theme.primaryColor || "#003366",
          secondaryColor: theme.secondaryColor || "#f8f9fa",
          showLogo: theme.showLogo !== undefined ? theme.showLogo : true,
        },
        labels: {
          documentTitle:
            labels.documentTitle ||
            (documentHeader as any)?.documentType?.replace("_", " ") ||
            "",
          taxName: labels.taxName || "VAT (16%)",
        },
        toggles: {
          format: toggles.format || "A4",
          showItemDescription:
            toggles.showItemDescription !== undefined
              ? toggles.showItemDescription
              : true,
          showTaxColumn:
            toggles.showTaxColumn !== undefined ? toggles.showTaxColumn : true,
          showBankDetails:
            toggles.showBankDetails !== undefined
              ? toggles.showBankDetails
              : true,
        },
        footer: {
          thankYouMessage:
            footer.thankYouMessage || "Thank you for your business!",
          bankDetails: footer.bankDetails || "",
          termsAndConditions: footer.termsAndConditions || "",
        },
      },
    };
  }, [documentHeader]);

  // Pure Web Browser Upload using Axios to Cloudinary
  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      setUploadError("File is too large. Max size is 1MB.");
      return;
    }

    setUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "GN_Products");

      const { data } = await axios.post(
        "https://api.cloudinary.com/v1_1/dsiyinydr/image/upload",
        formData,
      );

      if (data.secure_url) {
        setFieldValue("companySnapshot.logoUrl", data.secure_url);
      } else {
        setUploadError("Upload failed. Try again.");
      }
    } catch (err) {
      setUploadError(getErrorMessage(err, "Failed to upload logo image."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(documentHeaderActions.setServerStatus(null))}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: DocumentHeaderFormValues) => {
          const cleanedValues = cleanFormData(
            values as unknown as Record<string, unknown>,
          );
          dispatch(
            updateDocumentHeader({
              id: documentHeader.id,
              values: cleanedValues as unknown as DocumentHeaderFormValues,
            }),
          );
        }}
      >
        {({
          values,
          setFieldValue,
          dirty,
          isValid,
        }: FormikProps<DocumentHeaderFormValues>) => (
          <Form>
            <Stack spacing={4}>
              {/* --- SECTION 1: IDENTITY & LOGO --- */}
              <Box>
                <Typography sx={{ mb: 2, fontWeight: 700, fontSize: "1.1rem" }}>
                  1. Company Identity & Logo
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="companySnapshot.companyName"
                    label="Registered Name"
                  />
                  <FormTextField
                    name="companySnapshot.taxId"
                    label="Tax ID / PIN"
                  />
                  <FormTextField
                    name="companySnapshot.phone"
                    label="Phone Number"
                  />
                  <FormTextField
                    name="companySnapshot.email"
                    label="Email Address"
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <FormTextField
                    name="companySnapshot.address"
                    label="Physical Address"
                    multiline
                    rows={2}
                  />
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: (theme) =>
                      alpha(theme.palette.background.default, 0.4),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, display: "block", mb: 1.5 }}
                  >
                    DOCUMENT LOGO
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    sx={{ alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        bgcolor: "background.paper",
                      }}
                    >
                      {values.companySnapshot.logoUrl ? (
                        <img
                          src={values.companySnapshot.logoUrl}
                          alt="Logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          No Logo
                        </Typography>
                      )}
                    </Box>
                    <Stack spacing={1}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={
                          uploading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CloudUploadIcon />
                          )
                        }
                        disabled={uploading}
                      >
                        Upload New Logo
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, setFieldValue)}
                        />
                      </Button>
                      {values.companySnapshot.logoUrl && (
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() =>
                            setFieldValue("companySnapshot.logoUrl", "")
                          }
                        >
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {uploadError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {uploadError}
                    </Alert>
                  )}
                </Box>
              </Box>

              <Divider />

              {/* --- SECTION 2: VISUAL THEME --- */}
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2, alignItems: "center" }}
                >
                  <PaletteOutlinedIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    2. Visual Theme
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: 600, mb: 0.5 }}
                    >
                      Primary Brand Color
                    </Typography>
                    <input
                      type="color"
                      value={values.templateMetadata.theme.primaryColor}
                      onChange={(e) =>
                        setFieldValue(
                          "templateMetadata.theme.primaryColor",
                          e.target.value,
                        )
                      }
                      style={{
                        width: "100%",
                        height: "40px",
                        cursor: "pointer",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: 600, mb: 0.5 }}
                    >
                      Secondary Color
                    </Typography>
                    <input
                      type="color"
                      value={values.templateMetadata.theme.secondaryColor}
                      onChange={(e) =>
                        setFieldValue(
                          "templateMetadata.theme.secondaryColor",
                          e.target.value,
                        )
                      }
                      style={{
                        width: "100%",
                        height: "40px",
                        cursor: "pointer",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                      }}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.templateMetadata.theme.showLogo}
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.theme.showLogo",
                            e.target.checked,
                          )
                        }
                        color="primary"
                      />
                    }
                    label="Show Logo on Doc"
                  />
                </Box>
              </Box>

              <Divider />

              {/* --- SECTION 3: DOCUMENT CONFIG --- */}
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2, alignItems: "center" }}
                >
                  <SettingsOutlinedIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    3. Document Settings
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  }}
                >
                  <FormTextField
                    select
                    name="templateMetadata.toggles.format"
                    label="Print Format"
                  >
                    <MenuItem value="A4">Standard A4</MenuItem>
                    <MenuItem value="THERMAL_80MM">Thermal (80mm)</MenuItem>
                    <MenuItem value="THERMAL_58MM">Mobile (58mm)</MenuItem>
                  </FormTextField>
                  <FormTextField
                    name="templateMetadata.labels.documentTitle"
                    label="Custom Title"
                  />
                  <FormTextField
                    name="templateMetadata.labels.taxName"
                    label="Tax Label"
                  />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.templateMetadata.toggles.showTaxColumn}
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.toggles.showTaxColumn",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Show Tax Column"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          values.templateMetadata.toggles.showItemDescription
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.toggles.showItemDescription",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Show Item Desc"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          values.templateMetadata.toggles.showBankDetails
                        }
                        onChange={(e) =>
                          setFieldValue(
                            "templateMetadata.toggles.showBankDetails",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Show Bank Info"
                  />
                </Box>
              </Box>

              <Divider />

              {/* --- SECTION 4: FOOTER --- */}
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2, alignItems: "center" }}
                >
                  <TextFieldsIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    4. Footer & Terms
                  </Typography>
                </Stack>
                <Box
                  sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr" }}
                >
                  <FormTextField
                    name="templateMetadata.footer.thankYouMessage"
                    label="Greeting Message"
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    }}
                  >
                    <FormTextField
                      name="templateMetadata.footer.bankDetails"
                      label="Bank Details"
                      multiline
                      rows={3}
                      placeholder="Bank: ...&#10;Acc: ..."
                    />
                    <FormTextField
                      name="templateMetadata.footer.termsAndConditions"
                      label="Terms & Conditions"
                      multiline
                      rows={3}
                      placeholder="1. Goods once sold..."
                    />
                  </Box>
                </Box>
              </Box>

              <SubmitButton
                title="Save Configuration"
                loading={loading}
                disabled={!dirty || !isValid || loading || uploading}
                startIcon={<SaveOutlinedIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UpdateDocumentHeaderForm;
